import * as _ from 'lodash';
import { DOWNLOAD } from '../../../const';
import * as contentUtils from '../../../utils/contentUtils';

/** It is reserved 10% of the BookDownloadBar for the unzip time **/
const UNZIP_PERCENTAGE_OF_LOAD_BAR = 0;
const DOWNLOAD_PERCENTAGE_OF_LOAD_BAR = 1 - UNZIP_PERCENTAGE_OF_LOAD_BAR;

export function classroomDownloadProgress(contentBooks, activitiesContent, classId, booksSaving) {
  const progressData = [];
  _.forEach(contentBooks, contentBook => {
    if (contentBook.classId === classId) {
      progressData.push(downloadProgressData(contentBook, activitiesContent, contentBook.id, classId));
    }
  });
  const result = progressData.reduce(
    (previousValue, actualValue) => {
      return {
        isUnzipping: previousValue.isUnzipping || actualValue.isUnzipping,
        isDownloading: previousValue.isDownloading || actualValue.isDownloading,
        percentageSum: previousValue.percentageSum + actualValue.totalPercentage
      };
    },
    { isUnzipping: false, isDownloading: false, percentageSum: 0 }
  );
  return {
    isUnzipping: result.isUnzipping,
    isDownloading: result.isDownloading,
    totalPercentage: Math.floor(result.percentageSum / progressData.length)
  };
}

export function downloadProgressData(contentBook, activitiesContent, bookId, classId) {
  const activitiesContentDownloading = getBooksDownloadingActivitiesContent(activitiesContent, contentBook);
  let bookLocalProgress = JSON.parse(localStorage.getItem(`saveBook_${bookId}`));
  let totalDownloadPercentage = getDownloadPercentage(contentBook, activitiesContentDownloading);
  const booksUnzipping = contentUtils.getBooksUnzipping(classId, contentBook);
  const isUnzipping = booksUnzipping && totalDownloadPercentage > 0;
  const isDownloading = contentUtils.getBooksDownloading(classId, contentBook);
  if (_.isNil(bookLocalProgress)) {
    bookLocalProgress = 0;
  }
  if (totalDownloadPercentage < 101) {
    if (totalDownloadPercentage < bookLocalProgress) {
      totalDownloadPercentage = bookLocalProgress;
    } else {
      if (!_.isNil(bookId)) {
        localStorage.setItem(`saveBook_${bookId}`, totalDownloadPercentage);
        if (totalDownloadPercentage >= 99) {
          localStorage.removeItem(`saveBook_${bookId}`);
        }
      }
    }
  }
  return { isUnzipping, isDownloading, totalPercentage: totalDownloadPercentage };
}

function getBooksDownloadingActivitiesContent(activitiesContent, downloadingBook) {
  return _.filter(activitiesContent, activityContent => {
    return isBooksDownloadingActivityContent(activityContent, downloadingBook);
  });
}

function isBooksDownloadingActivityContent(activityContent, book) {
  let booksDownloadingActivityContent = false;
  if (book.id === activityContent.bookId && book.classId === activityContent.classId) {
    booksDownloadingActivityContent = true;
  }
  return booksDownloadingActivityContent;
}

function getDownloadPercentage(bookDownloading, activitiesContentDownloading) {
  if (bookDownloading) {
    let total = 0;

    addActivitiesContentToContentBooks(bookDownloading, activitiesContentDownloading);

    let allTotalSizesCalculated = areAllTotalSizesCalculated(bookDownloading);
    if (!allTotalSizesCalculated) return DOWNLOAD.EMPTY;

    let bookTotalSize = getContentBookTotalSize(bookDownloading);
    let bookCurrentPercentage = getContentBookCurrentPercentage(bookDownloading, bookTotalSize);

    if (bookCurrentPercentage > 0) total += bookCurrentPercentage;

    return Math.floor(total);
  }
}

function areAllTotalSizesCalculated(book) {
  return isContentBookTotalSizeReady(book) ? true : false;
}

function isContentBookTotalSizeReady(book) {
  return isCollectionTotalSizeReady(book.activitiesContent) || isCollectionTotalSizeReady(book.resources);
}

function isCollectionTotalSizeReady(collection) {
  let collectionLength = 0;
  let totalSizesReadyLength = 0;

  if (collection) {
    collectionLength = collection.length;
    collection.forEach(element => {
      if (element.totalSizeToDownload && element.totalSizeToDownload > 0) totalSizesReadyLength++;
    });
  }

  return collectionLength === totalSizesReadyLength;
}

function getContentBookTotalSize(book) {
  let contentBookTotalSize = 0;
  let activitiesContent = book.activitiesContent;
  let resources = book.resources;

  contentBookTotalSize += getCollectionTotalSize(activitiesContent);
  contentBookTotalSize += getCollectionTotalSize(resources);

  return contentBookTotalSize;
}

function getContentBookCurrentPercentage(book, bookTotalSize) {
  let bookCurrentPercentage = 0;
  let activitiesContent = book.activitiesContent;
  let resources = book.resources;
  let documentLessons = book.documentLessons;

  bookCurrentPercentage += getActivitiesContentCurrentPercentage(activitiesContent, bookTotalSize);
  bookCurrentPercentage += getCollectionCurrentPercentage(resources, bookTotalSize);
  bookCurrentPercentage += getCollectionCurrentPercentage(documentLessons, bookTotalSize);

  return bookCurrentPercentage;
}

function getCollectionTotalSize(collection) {
  let collectionSize = 0;
  if (collection) {
    collection.forEach(element => {
      if (element.totalSizeToDownload) collectionSize += element.totalSizeToDownload;
    });
  }
  return collectionSize;
}

function getCollectionCurrentPercentage(collection, bookTotalSize) {
  let collectionCurrentPercentage = 0;
  if (collection && bookTotalSize > 0) {
    collection.forEach(element => {
      if (element.downloadPercentage) {
        const elementPercentage =
          element.downloadPercentage === DOWNLOAD.FINISHED ? DOWNLOAD.COMPLETED : element.downloadPercentage;
        collectionCurrentPercentage += elementPercentage * (element.totalSizeToDownload / bookTotalSize); // Normalized
      }
    });
  }
  return collectionCurrentPercentage;
}

function getActivitiesContentCurrentPercentage(activitiesContent, bookTotalSize) {
  let bookActivitiesCurrentPercentageNormalized = 0;
  if (activitiesContent && bookTotalSize > 0) {
    activitiesContent.forEach(activityContent => {
      if (activityContent.downloadPercentage) {
        const activityCurrentDownloadPercentage =
          activityContent.downloadPercentage === DOWNLOAD.FINISHED
            ? DOWNLOAD.COMPLETED
            : activityContent.downloadPercentage;
        const activityCurrentUnzipPercentage =
          activityContent.unzipPercentage === DOWNLOAD.FINISHED ? DOWNLOAD.COMPLETED : activityContent.unzipPercentage;

        const activityCurrentMergedPercentage =
          DOWNLOAD_PERCENTAGE_OF_LOAD_BAR * activityCurrentDownloadPercentage +
          UNZIP_PERCENTAGE_OF_LOAD_BAR * activityCurrentUnzipPercentage;
        bookActivitiesCurrentPercentageNormalized +=
          activityCurrentMergedPercentage * (activityContent.totalSizeToDownload / bookTotalSize);
      }
    });
  }
  return bookActivitiesCurrentPercentageNormalized;
}

function addActivitiesContentToContentBooks(book, activitiesContentDownloading) {
  book.activitiesContent = contentUtils.getBookActivitiesContent(book, activitiesContentDownloading);
}
